import {
  Children,
  CSSProperties,
  FC,
  ReactElement,
  ReactNode,
  useEffect,
  useState,
} from 'react';

import classNames from 'classnames';
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react';
import { Swiper as ISwiper } from 'swiper/types';
import {
  A11y,
  Autoplay,
  EffectFade,
  FreeMode,
  Pagination,
} from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/autoplay';

import { Container, useData } from '@/components';

import { SliderButtons } from './Slider.components';

import styles from './Slider.module.scss';
import { ColorField } from '@prismicio/client';

interface ISliderProps extends SwiperProps {
  activatedHover?: boolean;
  activeAutoplay?: boolean;
  children: ReactNode[];
  heightShort?: boolean;
  seeAllElement?: ReactNode;
  showSliderButtons?: boolean;
  showSliderCardLink?: boolean;
  isSliderProductCard?: boolean;
  showSliderHeader?: boolean;
  showSlidesOutsideContainer?: boolean;
  nested?: boolean;
  sliderHeading?: ReactElement<any>;
  isNotPaddingMobile?: boolean;
  containerClassname?: string;
  showPagination?: boolean;
  paginationBackground?: ColorField;
  paginationProgressColor?: ColorField;
  /** Name of the accessible base of the carousel for the landmark */
  ariaLabel?: string;
}

const Slider: FC<ISliderProps> = ({
  activatedHover = false,
  activeAutoplay = false,
  sliderHeading,
  seeAllElement,
  children,
  className,
  heightShort = false,
  loop = false,
  slidesPerView,
  nested = false,
  containerClassname,
  showSliderButtons = true,
  showSliderCardLink = false,
  isSliderProductCard = false,
  showSliderHeader = true,
  showPagination = false,
  paginationBackground,
  paginationProgressColor,
  showSlidesOutsideContainer = false,
  isNotPaddingMobile,
  ariaLabel,
  ...props
}) => {
  const { productCard } = useData();
  const { isLargeCard, isProductCardLarge } = productCard.displayProps;

  const isProductsSlider = showSliderCardLink || isSliderProductCard;

  const baseLabel = ariaLabel ?? 'Carousel';
  const navigationLabel = `${baseLabel} navigation`;

  const [isPrevDisabled, setIsPrevDisabled] = useState<boolean>(true);
  const [isNextDisabled, setIsNextDisabled] = useState<boolean>(true);

  const [hideNavControls, setHideNavControls] = useState<boolean>(true);

  useEffect(() => {
    setHideNavControls(isPrevDisabled && isNextDisabled);
  }, [isPrevDisabled, isNextDisabled]);

  const handleSwiperChange = (swiper: ISwiper) => {
    setIsPrevDisabled(swiper.isBeginning);
    setIsNextDisabled(swiper.isEnd);

    if (!showSliderCardLink) {
      return;
    }

    const translate = swiper.snapGrid[swiper.snapGrid.length - 2] + 120;

    swiper.snapGrid[swiper.snapGrid.length - 1] = translate;
    swiper.init();

    if (swiper.isEnd) {
      swiper.setTranslate(translate * -1);
    }
  };

  const isHeader = !sliderHeading?.props?.children;

  const renderSwiper = () => (
    <Swiper
      a11y={
        showPagination && !isSliderProductCard
          ? {
              enabled: true,
              prevSlideMessage: 'Previous slide',
              nextSlideMessage: 'Next slide',
              firstSlideMessage: 'This is the first slide',
              lastSlideMessage: 'This is the last slide',
              paginationBulletMessage: 'Go to slide {{index}}',
            }
          : {
              enabled: true,
              prevSlideMessage: 'Previous slide',
              nextSlideMessage: 'Next slide',
              firstSlideMessage: 'This is the first slide',
              lastSlideMessage: 'This is the last slide',
            }
      }
      className={classNames(
        styles.slider,
        {
          [styles.slider__products]: isProductsSlider,
          [styles.slider__products_large]: isProductsSlider && isLargeCard,
          [styles.slider__products_large_variation]:
            isProductsSlider && isProductCardLarge,
          [styles.slider__height_short]: heightShort,
          [styles.slider__products_last]: isNextDisabled,
          [styles.slider__pagination]: showPagination,
        },
        className,
      )}
      loop={loop}
      loopAddBlankSlides={loop}
      modules={
        activeAutoplay
          ? [Autoplay, EffectFade, FreeMode, Pagination, A11y]
          : [EffectFade, FreeMode, Pagination, A11y]
      }
      nested={nested}
      onAfterInit={handleSwiperChange}
      onSlideChange={handleSwiperChange}
      onSwiper={handleSwiperChange}
      onTransitionEnd={handleSwiperChange}
      pagination={
        showPagination
          ? {
              horizontalClass: styles.slider__horizontal_pagination,
              type: 'progressbar',
            }
          : false
      }
      slidesPerView={slidesPerView ?? 'auto'}
      style={
        {
          '--swiper-pagination-color': paginationProgressColor,
          '--swiper-pagination-progressbar-bg-color': paginationBackground,
        } as CSSProperties
      }
      {...props}
    >
      <div
        className={classNames(styles.slider_header, {
          [styles.slider_header_right]: isHeader,
          [styles.slider_header_padding_mobile]: isNotPaddingMobile,
          [styles['slider_header--hide']]: !showSliderHeader,
          [styles['slider_header--showSlidesOutsideContainer']]:
            showSlidesOutsideContainer,
        })}
      >
        {sliderHeading}
        {!hideNavControls && (
          <div className={styles.slider__shop_all}>
            {seeAllElement}
            {showSliderButtons && (
              <SliderButtons
                ariaLabel={navigationLabel}
                isNextDisabled={isNextDisabled}
                isPrevDisabled={isPrevDisabled}
                loop={loop}
              />
            )}
          </div>
        )}
      </div>
      {Children.toArray(children).map((child, index) => (
        <SwiperSlide
          className={classNames(styles.slider_item, {
            [styles.slider_item_hover]: activatedHover,
          })}
          key={index}
        >
          {child}
        </SwiperSlide>
      ))}
    </Swiper>
  );

  return showSlidesOutsideContainer ? (
    <div
      className={classNames(styles.slider__container, containerClassname, {
        [styles.slider__container_not_padding_mobile]: isNotPaddingMobile,
      })}
    >
      {renderSwiper()}
    </div>
  ) : (
    <Container
      className={classNames(styles.slider__container, containerClassname, {
        [styles.slider__container_not_padding_mobile]: isNotPaddingMobile,
      })}
    >
      {renderSwiper()}
    </Container>
  );
};

export { Slider };
